import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    FileText, Folder, Download, Eye, Lock, Calendar, User,
    Image, Film, Music, FileCode, File, ExternalLink,
    ChevronDown, ChevronUp, Share2, Clock
} from 'lucide-react';
import '../styles/SharedView.css';

const SharedView = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shareData, setShareData] = useState(null);
    const [previewExpanded, setPreviewExpanded] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);

    const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5151' : 'https://fileapi.clockwisecpa.app';

    useEffect(() => {
        const fetchShareData = async () => {
            try {
                const response = await fetch(`${API_URL}/shares/${token}`);

                if (!response.ok) {
                    if (response.status === 404) {
                        setError('This share link is invalid or has expired.');
                    } else if (response.status === 410) {
                        setError('This share link has expired.');
                    } else {
                        setError('Failed to load shared content.');
                    }
                    setLoading(false);
                    return;
                }

                const data = await response.json();
                setShareData(data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching share:', err);
                setError('Failed to connect to server.');
                setLoading(false);
            }
        };

        fetchShareData();
    }, [token, API_URL]);

    const handleDownload = () => {
        window.open(`${API_URL}/shares/${token}/download`, '_blank');
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown size';
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = bytes;
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getFileIcon = (mimeType) => {
        if (!mimeType) return <File className="icon file" />;
        if (mimeType.startsWith('image/')) return <Image className="icon image" />;
        if (mimeType.startsWith('video/')) return <Film className="icon video" />;
        if (mimeType.startsWith('audio/')) return <Music className="icon audio" />;
        if (mimeType.includes('pdf')) return <FileText className="icon pdf" />;
        if (mimeType.includes('text') || mimeType.includes('json') || mimeType.includes('javascript') || mimeType.includes('xml')) {
            return <FileCode className="icon code" />;
        }
        return <FileText className="icon file" />;
    };

    const getFileTypeLabel = (mimeType) => {
        if (!mimeType) return 'File';
        if (mimeType.startsWith('image/')) return 'Image';
        if (mimeType.startsWith('video/')) return 'Video';
        if (mimeType.startsWith('audio/')) return 'Audio';
        if (mimeType.includes('pdf')) return 'PDF Document';
        if (mimeType.includes('word') || mimeType.includes('document')) return 'Document';
        if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'Spreadsheet';
        if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'Presentation';
        if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) return 'Archive';
        if (mimeType.includes('text')) return 'Text File';
        return 'File';
    };

    const canPreview = (mimeType) => {
        if (!mimeType) return false;
        return (
            mimeType.startsWith('image/') ||
            mimeType.startsWith('video/') ||
            mimeType.startsWith('audio/') ||
            mimeType.includes('pdf')
        );
    };

    const renderPreview = () => {
        const item = shareData?.file;
        const mimeType = item?.mime_type;
        const downloadUrl = `${API_URL}/shares/${token}/download`;

        if (!mimeType || !canPreview(mimeType)) {
            return (
                <div className="no-preview">
                    <File className="no-preview-icon" />
                    <p>Preview not available for this file type</p>
                    <span className="file-type-hint">{getFileTypeLabel(mimeType)}</span>
                </div>
            );
        }

        if (mimeType.startsWith('image/')) {
            return (
                <div className="preview-image-container">
                    {!imageLoaded && <div className="preview-loading">Loading preview...</div>}
                    <img
                        src={downloadUrl}
                        alt={item.original_name}
                        className={`preview-image ${imageLoaded ? 'loaded' : ''}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageLoaded(true)}
                    />
                </div>
            );
        }

        if (mimeType.startsWith('video/')) {
            return (
                <video controls className="preview-video">
                    <source src={downloadUrl} type={mimeType} />
                    Your browser does not support video playback.
                </video>
            );
        }

        if (mimeType.startsWith('audio/')) {
            return (
                <div className="preview-audio-container">
                    <div className="audio-visual">
                        <Music className="audio-icon" />
                        <span>{item.original_name}</span>
                    </div>
                    <audio controls className="preview-audio">
                        <source src={downloadUrl} type={mimeType} />
                        Your browser does not support audio playback.
                    </audio>
                </div>
            );
        }

        if (mimeType.includes('pdf')) {
            return (
                <div className="preview-pdf-container">
                    <iframe
                        src={downloadUrl}
                        className="preview-pdf"
                        title={item.original_name}
                    />
                </div>
            );
        }

        return null;
    };

    if (loading) {
        return (
            <div className="shared-view-container">
                <div className="shared-view-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading shared content...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shared-view-container">
                <div className="shared-view-error">
                    <div className="error-icon-wrapper">
                        <Lock className="error-icon" />
                    </div>
                    <h2>Access Denied</h2>
                    <p>{error}</p>
                    <a href="/" className="back-home-link">
                        <ExternalLink size={16} />
                        Go to TimeFiles
                    </a>
                </div>
            </div>
        );
    }

    const isFolder = shareData?.type === 'folder';
    const item = shareData?.file || shareData?.folder;
    const permission = shareData?.permission || 'view';
    const sharedBy = shareData?.shared_by;
    const canDownload = permission === 'download' || permission === 'edit';

    return (
        <div className="shared-view-container">
            <div className="shared-view-wrapper">
                {/* Header Bar */}
                <div className="shared-view-topbar">
                    <div className="topbar-brand">
                        <Share2 className="brand-icon" />
                        <span>TimeFiles</span>
                    </div>
                    <div className="topbar-info">
                        <User size={14} />
                        <span>Shared by {sharedBy || 'Anonymous'}</span>
                    </div>
                </div>

                <div className="shared-view-card">
                    {/* File/Folder Header */}
                    <div className="shared-view-header">
                        <div className="shared-item-icon-wrapper">
                            <div className={`shared-item-icon ${isFolder ? 'folder' : 'file'}`}>
                                {isFolder ? (
                                    <Folder className="icon" />
                                ) : (
                                    getFileIcon(item?.mime_type)
                                )}
                            </div>
                        </div>
                        <div className="shared-item-info">
                            <h1>{item?.original_name || item?.name || 'Shared Content'}</h1>
                            <div className="shared-item-meta">
                                {!isFolder && (
                                    <span className="meta-item type">
                                        {getFileTypeLabel(item?.mime_type)}
                                    </span>
                                )}
                                {!isFolder && item?.file_size && (
                                    <span className="meta-item size">
                                        {formatFileSize(item.file_size)}
                                    </span>
                                )}
                                {item?.created_at && (
                                    <span className="meta-item date">
                                        <Calendar size={14} />
                                        {formatDate(item.created_at)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Permission Badge */}
                    <div className="permission-section">
                        <div className={`permission-badge ${permission}`}>
                            {permission === 'view' && <Eye size={16} />}
                            {permission === 'download' && <Download size={16} />}
                            <span>
                                {permission === 'view' && 'View Only'}
                                {permission === 'download' && 'View & Download'}
                                {permission === 'edit' && 'View & Download'}
                            </span>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="shared-view-content">
                        {/* File Preview */}
                        {!isFolder && (
                            <div className="preview-section">
                                <button
                                    className="preview-toggle"
                                    onClick={() => setPreviewExpanded(!previewExpanded)}
                                >
                                    <span>Preview</span>
                                    {previewExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {previewExpanded && (
                                    <div className="preview-container">
                                        {renderPreview()}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Folder Contents */}
                        {isFolder && shareData?.contents && (
                            <div className="folder-contents">
                                <div className="contents-header">
                                    <h3>Contents</h3>
                                    <span className="contents-count">{shareData.contents.length} items</span>
                                </div>
                                {shareData.contents.length > 0 ? (
                                    <ul className="contents-list">
                                        {shareData.contents.map((contentItem, index) => (
                                            <li key={index} className="content-item">
                                                <div className={`content-icon-wrapper ${contentItem.type}`}>
                                                    {contentItem.type === 'folder' ? (
                                                        <Folder size={18} />
                                                    ) : (
                                                        <FileText size={18} />
                                                    )}
                                                </div>
                                                <span className="content-name">{contentItem.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-folder">
                                        <Folder className="empty-icon" />
                                        <p>This folder is empty</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="shared-view-actions">
                        {canDownload && !isFolder && (
                            <button onClick={handleDownload} className="download-btn primary">
                                <Download size={20} />
                                Download File
                            </button>
                        )}
                        {!canDownload && !isFolder && (
                            <div className="view-only-notice">
                                <Lock size={16} />
                                <span>This file is view-only and cannot be downloaded</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="shared-view-footer">
                    <p>Securely shared via <strong>TimeFiles</strong></p>
                </div>
            </div>
        </div>
    );
};

export default SharedView;
